import { Chart } from '@antv/g2';
import { FC, useEffect, useRef, useMemo } from 'react';
import LineChartOptions from './LineChartOptions';
import { ChartCommonProps, ChartConfig, applyConfig, } from '../../../utils/BaseChart/BaseChartOptions';


type LineChartProps = ChartCommonProps & {
    linesConfig: ChartConfig[];
    pointClickHandler?: (...args: any[]) => any;
};

const LineChart: FC<LineChartProps> = (props) => {
    const { className, data, linesConfig, sizeAttributes, viewLevelAdditionalProps, additionalMarks, pointClickHandler } = props;
    const chartContainer = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);

    // Memoize chart options to prevent unnecessary recalculations
    const chartOptions = useMemo(() => {
        const lineIDs = linesConfig.map((_, index) => `line${index + 1}`);
        const options = new LineChartOptions(data, lineIDs);

        // Set dimension attributes if available
        if (sizeAttributes) options.setAttributes(sizeAttributes);

        if(additionalMarks && additionalMarks.length) options.setChildren(additionalMarks);

        // Apply configurations for each line
        linesConfig.forEach((lineConfig, index) => {
            const lineKey = `line${index + 1}`;
            applyConfig(options, lineConfig, lineKey);
        });

        // Set view-level additional props if available
        if (viewLevelAdditionalProps) {
            options.setViewAdditionalProps(viewLevelAdditionalProps);
        }

        return options.getOptions();
    }, [data, linesConfig, sizeAttributes, viewLevelAdditionalProps, additionalMarks]);

    useEffect(() => {
        if (chartContainer.current) {
            chartInstance.current = new Chart({ container: chartContainer.current });
            chartInstance.current.options(chartOptions);
            pointClickHandler && chartInstance.current.on('point:click', pointClickHandler);
            chartInstance.current
                .render()
                .then(() => {
                    // Force layout recalculation after initial render to fix X-axis label wrap/ellipsis
                    chartInstance.current?.forceFit?.();
                })
                .catch((error) => console.error('Render failed', error));
        }

        return () => {
            chartInstance.current?.destroy();
        };
    }, [chartOptions]);

    // ResizeObserver: G2's autoFit only listens to window resize, not container resize.
    // When the right panel compresses (e.g. left panel expanded), the chart container shrinks
    // but the window doesn't resize. We must call forceFit() when the container resizes.
    useEffect(() => {
        const container = chartContainer.current;
        const chart = chartInstance.current;
        if (!container || !chart?.forceFit) return;

        const resizeObserver = new ResizeObserver(() => {
            chart.forceFit?.();
        });
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [chartOptions]);

    return <div ref={chartContainer} className={`w-full min-w-0 ${className}`} data-test={props['data-test']} data-asper-linechart/>;
};

export default LineChart;
